import React from 'react';
import { useTranslation } from 'react-i18next';

// Vignettes-maquettes des styles de couverture / 4e (formes CSS + la photo reellement posee) et
// ratio des vignettes selon le format. Sorti de AtelierCoverPanel.js le 2026-10-12.

// MIROIR de FORMAT_DIMENSIONS_MM (AtelierBookView.js/BookPreviewFinalLuxe.js)
// — meme convention de duplication assumee que COVER_COLORS/ALBUM_STYLES un
// peu plus haut. Sert uniquement a donner aux vignettes le bon RATIO (retour
// utilisateur, 2026-09-27 : "les miniatures doivent representer fidelement
// le rendu reel de la couverture") : sans ca, un livret carre (200x200)
// affichait des vignettes portrait comme un standard/luxe (210x280) — un
// format entier rendu faux d'un simple coup d'oeil.
export const COVER_RATIO_BY_FORMAT = {
  livret: 200 / 200,
  standard: 210 / 280,
  luxe: 210 / 280
};

// `photoUrl` (artefact "Atelier Bookipix" : "avec votre photo dans chaque
// vignette") — la photo REELLEMENT affichee sur la face courante
// (coverPhotoItem, voir BookAtelierLuxe.js), jamais une photo differente par
// carte : on compare des STYLES pour la MEME photo, pas une bibliotheque qui
// se repete — contrairement a la galerie "Mise en page" (AtelierLayoutPanel,
// ~20 cartes), ou le meme procede avait ete retire le meme jour (retour
// utilisateur : "au lieu de mettre des vraies photos et de les propager
// partout") : la ici, une poignee de cartes (6-7), une seule vraie photo,
// jamais de repetition confuse. Absente (aucune photo posee encore) -> un
// simple bloc de couleur, comportement d'avant.
// Zone photo d'une vignette : l'ICONE d'une image (jamais une vraie photo : on compare des FORMES de couverture).
function MiniPhoto({ className, style, withIcon }) {
  return (
    <span className={className} style={style}>
      {withIcon && (
        <svg className="cvrmini-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <circle cx="9" cy="10" r="1.8" />
          <path d="m21 16-5-5-8 8" />
        </svg>
      )}
    </span>
  );
}

export function CoverFormatMiniPreview({ shape, ratio, photoUrl }) {
  const { t } = useTranslation('atelier');
  const pageStyle = ratio ? { aspectRatio: ratio } : undefined;
  const photoStyle = (extra) => (photoUrl ? { ...extra, backgroundImage: `url('${photoUrl}')` } : extra);
  switch (shape) {
    case 'photo-band':
      return (
        <div className="cvrmini-page" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo" style={photoStyle({ height: '78%' })} withIcon={!photoUrl} />
          <span className="cvrmini-band" style={{ height: '22%' }}>
            <span className="cvrmini-line" style={{ width: '60%' }} />
          </span>
        </div>
      );
    case 'photo-full':
      return (
        <div className="cvrmini-page" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo cvrmini-photo-bg" style={photoStyle(undefined)} withIcon={!photoUrl} />
          <span className="cvrmini-scrim" />
          <span className="cvrmini-line cvrmini-line-on-photo" style={{ width: '55%' }} />
        </div>
      );
    case 'text-only':
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <span className="cvrmini-line" style={{ width: '70%' }} />
          <span className="cvrmini-rule" />
          <span className="cvrmini-line cvrmini-line-sm" style={{ width: '45%' }} />
        </div>
      );
    case 'photo-trio':
      return (
        <div className="cvrmini-page" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo" style={photoStyle({ height: '46%' })} />
          <span className="cvrmini-row" style={{ height: '24%' }}>
            <MiniPhoto className="cvrmini-photo" style={photoStyle(undefined)} withIcon={!photoUrl} />
            <MiniPhoto className="cvrmini-photo" style={photoStyle(undefined)} withIcon={!photoUrl} />
          </span>
          <span className="cvrmini-band" style={{ height: '30%' }}>
            <span className="cvrmini-line" style={{ width: '55%' }} />
          </span>
        </div>
      );
    case 'split':
      return (
        <div className="cvrmini-page cvrmini-row-full" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo" style={photoStyle({ width: '55%', height: '100%' })} withIcon={!photoUrl} />
          <span className="cvrmini-col">
            <span className="cvrmini-line" style={{ width: '75%' }} />
            <span className="cvrmini-line cvrmini-line-sm" style={{ width: '50%' }} />
          </span>
        </div>
      );
    case 'framed':
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo cvrmini-photo-framed" style={photoStyle(undefined)} withIcon={!photoUrl} />
          <span className="cvrmini-line" style={{ width: '55%' }} />
        </div>
      );
    case 'collab':
      return (
        <div className="cvrmini-page" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo cvrmini-photo-bg" style={photoStyle(undefined)} withIcon={!photoUrl} />
          <span className="cvrmini-collab-card">
            <span className="cvrmini-line cvrmini-line-sm" style={{ width: '60%' }} />
            <span className="cvrmini-avatars"><i /><i /></span>
          </span>
        </div>
      );
    case 'back-strip':
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <span className="cvrmini-row" style={{ height: '26%', width: '100%' }}>
            <MiniPhoto className="cvrmini-photo" style={photoStyle(undefined)} />
            <MiniPhoto className="cvrmini-photo" style={photoStyle(undefined)} withIcon={!photoUrl} />
            <MiniPhoto className="cvrmini-photo" style={photoStyle(undefined)} withIcon={!photoUrl} />
          </span>
          <span className="cvrmini-line cvrmini-line-italic" style={{ width: '60%' }} />
        </div>
      );
    case 'back-dark':
      return (
        <div className="cvrmini-page" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo cvrmini-photo-bg" style={photoStyle(undefined)} withIcon={!photoUrl} />
          <span className="cvrmini-veil" />
          <span className="cvrmini-line cvrmini-line-on-photo cvrmini-line-sm" style={{ width: '55%' }} />
        </div>
      );
    case 'back-contrib':
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <span className="cvrmini-avatars"><i /><i /><i /></span>
          <span className="cvrmini-line cvrmini-line-sm" style={{ width: '50%' }} />
        </div>
      );
    case 'back-minimal':
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <span className="cvrmini-line cvrmini-line-italic" style={{ width: '65%' }} />
          <span className="cvrmini-dot" />
        </div>
      );
    case 'back-stats':
      // Memes blocs que 'back-minimal' (2026-10-04 : le rendu reel ne
      // distingue plus les deux, voir backCoverRenderer.renderBackStats) —
      // la ligne de chiffres a disparu, cette vignette ne doit plus la
      // promettre.
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <span className="cvrmini-line cvrmini-line-italic" style={{ width: '65%' }} />
          <span className="cvrmini-dot" />
        </div>
      );
    case 'back-photo':
      return (
        <div className="cvrmini-page cvrmini-center" style={pageStyle}>
          <MiniPhoto className="cvrmini-photo cvrmini-photo-small" style={photoStyle(undefined)} withIcon={!photoUrl} />
          <span className="cvrmini-line cvrmini-line-italic" style={{ width: '55%' }} />
        </div>
      );
    default:
      return (
        <div className="cvrmini-page cvrmini-auto" style={pageStyle}>
          <span>{t('coverPanel.autoPreviewLabel')}</span>
        </div>
      );
  }
}

export function FormatPhotoBadge({ title }) {
  return (
    <span className="coverlite-format-photo-badge" title={title} aria-hidden="true">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="8.5" cy="10.5" r="1.5" />
        <path d="M21 15l-5-5L5 19" />
      </svg>
    </span>
  );
}
